import { Stack } from '@mui/material'
import { pick } from 'ramda'
import { useParams } from 'react-router-dom'

import { HistoricList, PublishDialog } from '@/components'
import { useComponentProvider } from '@/components/ViewComponentModal/ViewComponentProvider'
import { useAppsyncRefetch, useListHistory, useMySnackbar } from '@/hooks'
import {
  useGetCustomComponent,
  useUpdateCustomComponent,
} from '@/screens/ComponentLibrary/hooks'
import {
  CustomComponentEnhanced,
  storeKey,
} from '@/screens/ComponentLibrary/hooks/CONSTANTS'
import { CreateVailHistoryInput, ProcessingStatus } from '@/utils/api'

import { SemanticSearchSettings } from '../components'
import { useGenerateEmbeddings } from '../hooks/useGenerateEmbeddings'
import { parseSemanticSearchData } from './ConfigData'

interface SemanticSearch
  extends CustomComponentEnhanced<SemanticSearchSettings> {}

export const History = () => {
  const { orgID } = useParams<{
    orgID: string
  }>()
  const { componentID } = useComponentProvider()
  const snackbars = useMySnackbar({
    errorMessage: 'Embeddings generation failed',
    infoMessage: 'Embeddings generation in progress',
    successMessage: 'Embeddings generated successfully',
  })

  const queryKey = [storeKey, componentID, 'history']
  const generateEmbed = useGenerateEmbeddings(componentID)
  const updateSemanticSearch =
    useUpdateCustomComponent<SemanticSearchSettings>(componentID)
  const currentSemanticSearch =
    useGetCustomComponent<SemanticSearchSettings>(componentID)
  const historyQuery = useListHistory(
    queryKey,
    'customComponent',
    { id: componentID },
    parseSemanticSearchData
  )

  const parseData = (data?: SemanticSearch | null) => {
    if (!data) return {}
    return pick(['name', 'description', 'settings', 'type'], data)
  }

  const restoreVersion = async (data: Record<string, unknown>) => {
    await updateSemanticSearch.mutateAsync({
      ...parseData(data as unknown as SemanticSearch),
    } as unknown as SemanticSearch)
  }
  const generateEmbeddings = async (data: Partial<CreateVailHistoryInput>) => {
    try {
      snackbars.setInfoSnackbar()

      await updateSemanticSearch.mutateAsync({
        displayStatus: 'Generating embeddings',
        status: ProcessingStatus.processing,
      })

      const pineconeNamespace = [
        currentSemanticSearch.data?.org?.slug,
        currentSemanticSearch.data?.settings.largeLanguageModelSettings
          .pineconeNamespace,
        data.name,
      ].join('_')

      return {
        data: JSON.stringify({
          description: currentSemanticSearch.data?.description,
          name: currentSemanticSearch.data?.name,
          settings: {
            ...currentSemanticSearch.data?.settings,
            largeLanguageModelSettings: {
              ...currentSemanticSearch.data?.settings
                .largeLanguageModelSettings,
              pineconeNamespace,
            },
          },
          type: 'SemanticSearch',
        }),
      } as Partial<CreateVailHistoryInput>
    } catch (e) {
      snackbars.setErrorSnackbar()
      throw e
    }
  }
  useAppsyncRefetch((state) => state.refetchStatus(componentID))
  return (
    <Stack height="70vh">
      <HistoricList
        data={historyQuery.data}
        extraColumns={[
          {
            field: 'Status',
            flex: 1,
            headerName: 'Status',
            valueGetter: (_, { id, data }) => {
              const status = JSON.parse(data)?.status
              if (!!status) return status
              if (id === historyQuery.data?.[0].id)
                return currentSemanticSearch.data?.displayStatus
              return 'Generated'
            },
          },
        ]}
        restore={restoreVersion}
      />
      <PublishDialog
        customDialogOnSubmit={generateEmbeddings}
        dataToSave={parseData(currentSemanticSearch.data)}
        extraInfo={{
          customComponentHistoryId: componentID,
          organizationHistoryId: orgID,
        }}
        queryKey={queryKey}
        onEnd={async (data) => {
          if (data.createVailHistory?.id)
            await generateEmbed.mutateAsync({
              id: componentID,
              versionId: data.createVailHistory.id,
            })
          else snackbars.setErrorSnackbar('Failed to generate embeddings')
        }}
      />
    </Stack>
  )
}
